import Button from "@amzn/meridian/button";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import SearchField from "@amzn/meridian/search-field";
import Text from "@amzn/meridian/text";
import React, { useEffect, useState } from "react";
import RecordLine from "src/components/shared/recordLine/recordLine";
import ScrollableSection from "src/components/shared/scrollableSection/scrollableSection";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  getAutomatedJobs,
  setSelectedJob,
  clearSelectedJob,
  upsertBatchJobDefinition,
  getDataSources,
} from "src/store/automatedBatchJobSlice";
import { BatchJobDefinition } from "src/services/automatedJobsService";
import AutomatedJobForm from "./components/automatedJobForm";
import { getModels } from "src/store/modelManagementSlice";

const AutomatedJobManagement = () => {
  const dispatch = useAppDispatch();

  const { automatedJobs, getAutomatedJobsloading, dataSources, getDataSourcesLoading } =
    useAppSelector((state) => state.automatedBatchJobSlice);

  const { modelView } = useAppSelector((state) => state.modelSlice);

  const [filteredJobs, setFilteredJobs] = useState<BatchJobDefinition[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [isEditingJob, setIsEditingJob] = useState(false);

  // Effect for loading automated jobs - only if not already loaded or loading
  useEffect(() => {
    if (automatedJobs.length === 0 && getAutomatedJobsloading === "idle") {
      dispatch(getAutomatedJobs());
    }
  }, [automatedJobs.length, getAutomatedJobsloading]);

  // Effect for loading models - only if not already loaded or loading
  useEffect(() => {
    if (modelView.length === 0) {
      dispatch(getModels());
    }
  }, [modelView.length]);

  // Effect for loading data sources - only if not already loaded or loading
  useEffect(() => {
    if (dataSources.length === 0 && getDataSourcesLoading === "idle") {
      dispatch(getDataSources());
    }
  }, [dataSources.length, getDataSourcesLoading]);

  useEffect(() => {
    filterJobs();
  }, [searchValue, automatedJobs]);

  const filterJobs = () =>
    setFilteredJobs(
      searchValue
        ? automatedJobs.filter((job) => job.name.toLowerCase().includes(searchValue.toLowerCase()))
        : automatedJobs,
    );

  const handleJobSelect = (job: BatchJobDefinition) => {
    setSelectedJobId(job.id || null);
    setIsCreatingNew(false);
    setIsEditingJob(false);
    dispatch(setSelectedJob(job));
  };

  const handleCreateNew = () => {
    dispatch(clearSelectedJob());
    setSelectedJobId(null);
    setIsCreatingNew(true);
    setIsEditingJob(false);
  };

  const handleEditJob = () => {
    setIsEditingJob(true);
  };

  const handleCancelEdit = () => {
    setIsEditingJob(false);
  };

  const handleCancelCreate = () => {
    setIsCreatingNew(false);
  };

  const handleSaveEdit = async (formData: any) => {
    try {
      await dispatch(
        upsertBatchJobDefinition({
          jobData: formData,
          id: selectedJobId || undefined,
        }),
      ).unwrap();
      setIsEditingJob(false);
    } catch (error) {
      console.error("Error saving job:", error);
    }
  };

  const selectedJob = automatedJobs.find((job) => job.id === selectedJobId);

  const handleCreateSubmit = async (formData: any) => {
    try {
      await dispatch(
        upsertBatchJobDefinition({
          jobData: formData,
        }),
      ).unwrap();
      setIsCreatingNew(false);
    } catch (error) {
      console.error("Error creating job:", error);
    }
  };

  const renderRightPanel = () => {
    if (selectedJob) {
      return (
        <AutomatedJobForm
          job={selectedJob}
          mode={isEditingJob ? "edit" : "view"}
          onEdit={handleEditJob}
          onCancel={handleCancelEdit}
          onSubmit={handleSaveEdit}
        />
      );
    }

    if (isCreatingNew) {
      return (
        <AutomatedJobForm
          mode="create"
          onCancel={handleCancelCreate}
          onSubmit={handleCreateSubmit}
        />
      );
    }

    return (
      <Column spacing="600" alignmentHorizontal="center" alignmentVertical="center">
        <Text type="h200" color="secondary">
          Select an automated job to view details
        </Text>
        <Text color="secondary">
          Choose a job from the list on the left to see its configuration and manage it.
        </Text>
      </Column>
    );
  };

  return (
    <Row
      alignmentVertical="top"
      widths={["grid-3", "fit", "fill"]}
      className="automated-jobs-management"
    >
      <ScrollableSection spacingInset="500">
        <Column spacing="400">
          <Row alignmentVertical="center" widths={["fill", "fit"]}>
            <Text type="h300">Automated Jobs</Text>
            <Button type="primary" onClick={handleCreateNew}>
              Create New Job
            </Button>
          </Row>
          <SearchField onChange={setSearchValue} value={searchValue} onSubmit={filterJobs} />
        </Column>
        <Column spacing={"300"}>
          {getAutomatedJobsloading === "pending" ? (
            <Text>Loading jobs...</Text>
          ) : filteredJobs.length === 0 ? (
            <Text>No automated jobs found.</Text>
          ) : (
            filteredJobs.map((job) => (
              <RecordLine
                key={job.id}
                name={job.name}
                description={`Status: ${job.status} | Model: ${job.modelId}`}
                isSelected={selectedJobId === job.id}
                onClick={() => handleJobSelect(job)}
                data-cy={"automated-job__list"}
              />
            ))
          )}
        </Column>
      </ScrollableSection>
      <div className="split-line"></div>
      <ScrollableSection spacingInset="500">
        <Column spacing="600">{renderRightPanel()}</Column>
      </ScrollableSection>
    </Row>
  );
};

export default AutomatedJobManagement;
